import React, { useEffect, useState } from 'react';

import AdminDataTable from './AdminDataTable';
import { formatAdminDate } from './adminFormatting';
import { Button, Card } from '@copilot/ui';
import * as adminApi from '../../services/adminApi';
import { formatPlanPrice } from '../../services/pricingApi';

// Every row returned by GET /api/admin/subscription-change-requests is
// PENDING by construction (the backend query filters to it), so both
// actions are always offered - there is no separate "is this pending"
// check to make here.
const COLUMNS = (onApprove, onReject, busyId) => [
  {
    key: 'user',
    label: 'User',
    render: (request) => (
      <>
        <div>{request.user.full_name}</div>
        <div className="admin-table-subtext">{request.user.email}</div>
      </>
    ),
  },
  {
    key: 'current_plan',
    label: 'Current Plan',
    render: (request) => request.current_plan?.name || '—',
  },
  {
    key: 'requested_plan',
    label: 'Requested Plan',
    render: (request) => {
      const price =
        request.billing_cycle === 'yearly'
          ? request.requested_plan.yearly_price
          : request.requested_plan.monthly_price;
      return `${request.requested_plan.name} (${formatPlanPrice(price, request.requested_plan.currency)})`;
    },
  },
  {
    key: 'created_at',
    label: 'Requested At',
    render: (request) => formatAdminDate(request.created_at),
  },
  {
    key: 'actions',
    label: 'Actions',
    render: (request) => (
      <div className="admin-request-actions">
        <Button
          variant="primary"
          onClick={() => onApprove(request)}
          disabled={busyId === request.id}
        >
          Approve
        </Button>
        <Button
          variant="danger"
          onClick={() => onReject(request)}
          disabled={busyId === request.id}
        >
          Reject
        </Button>
      </div>
    ),
  },
];

// Integrated into the existing Pricing admin page (AdminPricingPage.jsx)
// rather than a separate route/nav item - this is the smallest UI that
// lets an admin review requests without introducing a new subsystem.
function SubscriptionChangeRequestsPanel() {
  const [requests, setRequests] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionError, setActionError] = useState('');
  const [actionMessage, setActionMessage] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setIsLoading(true);
      setError('');

      try {
        const data = await adminApi.getSubscriptionChangeRequests();
        if (!cancelled) setRequests(data.items);
      } catch (err) {
        if (!cancelled) setError(err.message || 'Unable to load plan-change requests.');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    load();

    return () => {
      cancelled = true;
    };
  }, [reloadToken]);

  const notify = (message) => {
    setActionMessage(message);
    setTimeout(() => setActionMessage(''), 2500);
  };

  const handleApprove = async (request) => {
    setActionError('');
    setBusyId(request.id);

    try {
      await adminApi.approveSubscriptionChangeRequest(request.id);
      notify('Plan change approved.');
      setReloadToken((token) => token + 1);
    } catch (err) {
      setActionError(err.message || 'Unable to approve this request.');
    } finally {
      setBusyId(null);
    }
  };

  const handleReject = async (request) => {
    setActionError('');
    setBusyId(request.id);

    try {
      await adminApi.rejectSubscriptionChangeRequest(request.id);
      notify('Plan change rejected.');
      setReloadToken((token) => token + 1);
    } catch (err) {
      setActionError(err.message || 'Unable to reject this request.');
    } finally {
      setBusyId(null);
    }
  };

  // Stays out of the way of the plans table below when there is nothing
  // to review - no loading/error state has ever been reached either, and
  // no just-completed action's own success message is still showing (it
  // would otherwise vanish the instant the now-empty list reloads).
  if (!isLoading && !error && requests.length === 0 && !actionMessage) {
    return null;
  }

  return (
    <Card interactive title="Pending Plan Change Requests" className="admin-section-heading">
      {actionError && <div className="form-alert">{actionError}</div>}
      {actionMessage && <p className="admin-action-status success">{actionMessage}</p>}

      {error ? (
        <div className="form-alert">{error}</div>
      ) : (
        <AdminDataTable
          columns={COLUMNS(handleApprove, handleReject, busyId)}
          rows={requests}
          getRowKey={(request) => request.id}
          isLoading={isLoading}
          loadingLabel="Loading requests..."
          emptyTitle="No pending requests."
        />
      )}
    </Card>
  );
}

export default SubscriptionChangeRequestsPanel;
